/**
 * FyEndSurgeSection — Q4 spending-surge visualization.
 *
 * Phase B3 migration: internals swapped from Recharts AreaChart to
 * `CalendarHeatmapRenderer`. The mart returns one row per (FY, fiscal_month).
 * We project each (FY, fiscal_month) pair into a calendar date — federal
 * fiscal months 1-12 map to Oct (FY-1) through Sep (FY) — and pass [date, $]
 * tuples to the renderer's `points` API. ECharts paints one cell per painted
 * day; the FY-end concentration shows up as the bright vertical strip on the
 * Jul-Sep months.
 *
 * Also keeps the Q4-share KPI chip (red >35%, orange >25%, green ≤25%) above
 * the calendar so the answer is immediate even if the heatmap is small.
 */
import { useMemo } from 'react'
import { govdataAPI } from '../../services/govdata'
import { useGovdata } from './useGovdata'
import SectionCard from './SectionCard'
import CalendarHeatmapRenderer from '../exec/generic/CalendarHeatmapRenderer'
import type { DashboardFilters } from '../../types/govdata'
import { parseNum } from '../../types/govdata'

interface Props {
  filters: DashboardFilters
  embedded?: boolean
}

/** Federal fiscal month → calendar [year_offset, month] (1=Jan). */
function fiscalToCalendar(fy: number, fm: number): [number, number] {
  // FM 1 = Oct of (FY-1), FM 4 = Jan of FY, FM 12 = Sep of FY.
  if (fm <= 3) return [fy - 1, 9 + fm]   // Oct/Nov/Dec
  return [fy, fm - 3]                     // Jan..Sep
}

/** Format ISO date YYYY-MM-DD (mid-month sentinel for the renderer). */
function fmtDate(year: number, month: number): string {
  const mm = String(month).padStart(2, '0')
  return `${year}-${mm}-15`
}

export default function FyEndSurgeSection({ filters, embedded = false }: Props) {
  const paramsKey = JSON.stringify(filters)
  const { data, loading, error } = useGovdata(
    () => govdataAPI.fyEndSurge(filters),
    paramsKey,
  )

  const { points, range, q4Share } = useMemo(() => {
    type Acc = { obligated: number; awards: number; isQ4: boolean }
    // Aggregate offices to one bucket per (FY, fiscal_month).
    const byKey = new Map<string, Acc>()
    for (const r of data) {
      const k = `${r.fiscal_year}|${r.fiscal_month}`
      const e = byKey.get(k) || { obligated: 0, awards: 0, isQ4: !!r.is_q4 }
      e.obligated += parseNum(r.total_obligated)
      e.awards += r.award_count || 0
      byKey.set(k, e)
    }

    const pts: [string, number][] = []
    let totalObl = 0
    let q4Obl = 0
    for (const [k, e] of byKey) {
      const [fyStr, fmStr] = k.split('|')
      const [year, month] = fiscalToCalendar(Number(fyStr), Number(fmStr))
      pts.push([fmtDate(year, month), e.obligated])
      totalObl += e.obligated
      if (e.isQ4) q4Obl += e.obligated
    }

    pts.sort((a, b) => a[0].localeCompare(b[0]))
    const r: [string, string] | undefined =
      pts.length > 0 ? [pts[0][0], pts[pts.length - 1][0]] : undefined
    const share = totalObl > 0 ? ((q4Obl / totalObl) * 100).toFixed(1) : '0'
    return { points: pts, range: r, q4Share: share }
  }, [data])

  const q4Num = parseFloat(q4Share)

  return (
    <SectionCard
      title="Fiscal Year-End Surge"
      subtitle="Monthly obligated $ on a calendar — FY-end concentration shows as Jul-Sep heat"
      loading={loading}
      error={error}
      embedded={embedded}
    >
      {points.length === 0 ? (
        <div className={`${embedded ? 'h-full' : 'h-64'} flex items-center justify-center text-sm text-gray-400`}>No data</div>
      ) : (
        <div className={embedded ? 'flex flex-col h-full min-h-0' : ''}>
          <div className="flex gap-3 mb-3 text-xs shrink-0">
            <div
              className={`px-2 py-1 rounded ${
                q4Num > 35
                  ? 'bg-red-50 text-red-700'
                  : q4Num > 25
                    ? 'bg-orange-50 text-orange-700'
                    : 'bg-green-50 text-green-700'
              }`}
            >
              Q4 Share: <span className="font-semibold">{q4Share}%</span>
              <span className="text-[10px] ml-1">(healthy ≤25%)</span>
            </div>
          </div>
          <div className={embedded ? 'flex-1 min-h-0' : 'h-72'}>
            <CalendarHeatmapRenderer
              data={[]}
              metrics={['total_obligated']}
              points={points}
              range={range}
            />
          </div>
        </div>
      )}
    </SectionCard>
  )
}
